import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { VortexDemo } from "./ui/vortex-demo";
import { Spotlight } from "./ui/spotlight";
import { BackgroundBeams } from "./ui/background-beams";
import { CardSpotlight } from "./ui/card-spotlight";
import { useTheme } from "../contexts/ThemeContext";

const services = [
  {
    title: "Full Web Suite",
    description: "Complete digital solutions that power your business and delight your users.",
    bullets: [
      "Custom Website Development",
      "E-commerce & Online Stores",
      "Responsive Mobile Design"
    ]
  },
  {
    title: "Business Automation",
    description: "Streamline operations with intelligent automation solutions.",
    bullets: [
      "AI-Powered POS Systems",
      "Inventory Management",
      "Customer Relationship Tools",
      "Automated Workflows",
      "Real-time Analytics"
    ]
  },
  {
    title: "Full Graphic Design",
    description: "Professional printing and design services for online and offline marketing.",
    bullets: [
      "Custom Printing Services",
      "Business Cards & Stationery",
      "Catalogues & Brochures",
      "Packaging & Label Design",
      "Large Format Printing"
    ]
  },
  {
    title: "App Dev",
    description: "Cutting-edge mobile and web applications built for the future.",
    bullets: [
      "iOS & Android Apps",
      "Progressive Web Apps",
      "Custom Backend Systems",
      "API Development",
      "Cross-Platform Solutions"
    ]
  },
];

interface ServicesPageProps {
  setVortexActive?: (active: boolean) => void;
}

const ServicesPage: React.FC<ServicesPageProps> = ({ setVortexActive }) => {
  const { theme } = useTheme();
  const location = useLocation();
  const [activeServiceOverlay, setActiveServiceOverlay] = useState<string | null>(null);
  const [cardsDropped, setCardsDropped] = useState(false);
  const [showCherryVideo, setShowCherryVideo] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [fadeOutImage, setFadeOutImage] = useState(false);
  const [fadeOutVideo, setFadeOutVideo] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [graphicCountdown, setGraphicCountdown] = useState(0); // 0: not started, 1: End to End, 2: Graphics, 3: Video, 4: show video

  // Check for navigation state to auto-open overlay
  useEffect(() => {
    if (location.state && (location.state as any).openOverlay) {
      const overlayToOpen = (location.state as any).openOverlay;
      setActiveServiceOverlay(overlayToOpen);
      setCardsDropped(true);
      if (setVortexActive) setVortexActive(true);
    }
  }, [location.state, setVortexActive]);

  // Start countdown when overlay opens
  React.useEffect(() => {
    if (activeServiceOverlay === "Full Graphic Design" && !showCherryVideo) {
      setGraphicCountdown(1);
      const timers = [
        setTimeout(() => setGraphicCountdown(2), 1000),
        setTimeout(() => setGraphicCountdown(3), 2000),
        setTimeout(() => setGraphicCountdown(4), 3000),
      ];
      return () => timers.forEach(clearTimeout);
    } else {
      setGraphicCountdown(0);
  }

  }, [activeServiceOverlay, showCherryVideo]);

  // When countdown reaches 4, show the video overlay
  React.useEffect(() => {
    if (graphicCountdown === 4) {
      setShowCherryVideo(true);
      setVideoReady(false);
      setFadeOutImage(false);
      setFadeOutVideo(false);
      setVideoEnded(false);
    }
  }, [graphicCountdown]);

  // When video is ready, fade out the image
  React.useEffect(() => {
    if (showCherryVideo && videoReady) {
      setTimeout(() => setFadeOutImage(true), 50); // slight delay for smoothness
    }
  }, [showCherryVideo, videoReady]);

  // When video ends, wait 1s then fade out the video
  React.useEffect(() => {
    if (videoEnded) {
      setTimeout(() => setFadeOutVideo(true), 1000);
    }
  }, [videoEnded]);

  const handleCardClick = (title: string) => {
    if (title === "Full Web Suite") {
      setActiveServiceOverlay(title);
      setCardsDropped(true);
      if (setVortexActive) setVortexActive(true);
    } else if (title === "Business Automation") {
      setActiveServiceOverlay(title);
      setCardsDropped(true);
      if (setVortexActive) setVortexActive(true);
    } else if (title === "Full Graphic Design") {
      setActiveServiceOverlay(title);
      setCardsDropped(true);
      if (setVortexActive) setVortexActive(true);
    } else if (title === "App Dev") {
      setActiveServiceOverlay(title);
      setCardsDropped(true);
      if (setVortexActive) setVortexActive(true);
    }
  };

  const handleReturn = () => {
    setActiveServiceOverlay(null);
    setTimeout(() => {
      setCardsDropped(false);
      if (setVortexActive) setVortexActive(false);
    }, 800);
  };

  const isLightTheme = theme === "light";
  const servicesShellStyle = {
    background: isLightTheme
      ? 'radial-gradient(circle at 76% 8%, rgba(20,184,166,0.24), transparent 32%), radial-gradient(circle at 18% 70%, rgba(56,189,248,0.14), transparent 30%), linear-gradient(135deg, #f8fafc 0%, #eef7f8 48%, #f6fbfb 100%)'
      : 'radial-gradient(circle at 76% 8%, rgba(249,115,22,0.24), transparent 32%), radial-gradient(circle at 18% 70%, rgba(251,146,60,0.12), transparent 30%), linear-gradient(135deg, #050505 0%, #0b0b0d 52%, #17100b 100%)',
    ['--text-primary' as string]: isLightTheme ? '#06131a' : '#fff7ed',
    ['--text-secondary' as string]: isLightTheme ? 'rgba(8,47,73,0.66)' : 'rgba(255,237,213,0.68)',
    ['--accent-primary' as string]: isLightTheme ? '#0f766e' : '#f97316',
    ['--accent-secondary' as string]: isLightTheme ? '#38bdf8' : '#f59e0b',
    ['--border-primary' as string]: isLightTheme ? 'rgba(20,184,166,0.3)' : 'rgba(251,146,60,0.28)',
    ['--card-bg' as string]: isLightTheme ? 'rgba(255,255,255,0.72)' : 'rgba(8,7,6,0.72)',
  };
  const serviceSpotlightClass = isLightTheme
    ? '!rounded-none !border-teal-400/25 !bg-white !bg-none !p-0 !shadow-[0_0_42px_rgba(20,184,166,0.14),0_20px_70px_rgba(8,47,73,0.08)]'
    : '!rounded-none !border-orange-300/20 !bg-[#050505] !bg-none !p-0 !shadow-[0_0_40px_rgba(249,115,22,0.14)]';
  const serviceCardWashClass = isLightTheme
    ? 'pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_0%,rgba(20,184,166,0.18),transparent_34%)] opacity-80'
    : 'pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_0%,rgba(249,115,22,0.16),transparent_34%)] opacity-70';
  const serviceCardLineClass = isLightTheme
    ? 'pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-teal-300/80 to-transparent'
    : 'pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-orange-200/70 to-transparent';
  const serviceIconShadow = isLightTheme ? '0 0 22px rgba(20,184,166,0.24)' : '0 0 22px rgba(249,115,22,0.24)';
  const serviceBadgeClass = isLightTheme
    ? 'absolute right-4 top-4 z-20 border border-teal-400/30 bg-teal-50/90 px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-teal-800 shadow-[0_0_18px_rgba(20,184,166,0.14)]'
    : 'absolute right-4 top-4 z-20 border border-orange-300/35 bg-orange-500/15 px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-orange-100 shadow-[0_0_18px_rgba(249,115,22,0.16)]';
  const serviceReturnButtonClass = isLightTheme
    ? 'fixed top-20 right-6 z-50 border border-teal-400/35 bg-white/70 px-6 py-3 font-black uppercase tracking-[0.14em] text-teal-800 backdrop-blur-sm transition-all duration-300 hover:border-teal-500/70 hover:bg-teal-50'
    : 'fixed top-20 right-6 z-50 border border-orange-300/35 bg-orange-500/10 px-6 py-3 font-black uppercase tracking-[0.14em] text-orange-100 backdrop-blur-sm transition-all duration-300 hover:border-orange-200/80 hover:bg-orange-500/20';
  const suiteTitleClass = isLightTheme
    ? 'mb-6 bg-gradient-to-r from-teal-700 via-sky-500 to-cyan-400 bg-clip-text text-4xl font-black text-transparent md:text-6xl'
    : 'mb-6 bg-gradient-to-r from-orange-300 via-orange-500 to-amber-300 bg-clip-text text-4xl font-black text-transparent md:text-6xl';
  const suiteCardClass = isLightTheme
    ? 'premium-card-reveal border border-teal-400/24 p-6 text-white shadow-[0_0_34px_rgba(20,184,166,0.12),inset_0_1px_0_rgba(204,251,241,0.1)]'
    : 'premium-card-reveal border border-orange-300/20 p-6 text-orange-50 shadow-[0_0_34px_rgba(249,115,22,0.1),inset_0_1px_0_rgba(255,237,213,0.08)]';
  const suiteIconClass = isLightTheme
    ? 'mx-auto mb-4 flex h-12 w-12 items-center justify-center bg-gradient-to-r from-teal-500 to-sky-400 shadow-[0_0_18px_rgba(20,184,166,0.26)]'
    : 'mx-auto mb-4 flex h-12 w-12 items-center justify-center bg-gradient-to-r from-orange-500 to-amber-400 shadow-[0_0_18px_rgba(249,115,22,0.28)]';
  const suiteFeatureTitleClass = isLightTheme ? 'mb-2 text-lg font-black text-teal-300' : 'mb-2 text-lg font-black text-orange-300';
  const suiteMutedTextClass = 'text-sm text-white/76';
  const graphicTitleClass = isLightTheme
    ? 'mb-6 bg-gradient-to-r from-teal-300 via-sky-300 to-cyan-200 bg-clip-text text-4xl font-black text-transparent md:text-6xl'
    : 'mb-6 bg-gradient-to-r from-orange-300 via-orange-500 to-amber-300 bg-clip-text text-4xl font-black text-transparent md:text-6xl';
  const graphicCardClass = isLightTheme
    ? 'premium-card-reveal border border-teal-400/24 p-6 text-white shadow-[0_0_34px_rgba(20,184,166,0.12),inset_0_1px_0_rgba(204,251,241,0.1)]'
    : 'premium-card-reveal border border-orange-300/20 p-6 text-orange-50 shadow-[0_0_34px_rgba(249,115,22,0.1),inset_0_1px_0_rgba(255,237,213,0.08)]';
  const graphicIconClass = isLightTheme
    ? 'mx-auto mb-4 flex h-12 w-12 items-center justify-center bg-gradient-to-r from-teal-500 to-sky-400 shadow-[0_0_18px_rgba(20,184,166,0.26)]'
    : 'mx-auto mb-4 flex h-12 w-12 items-center justify-center bg-gradient-to-r from-orange-500 to-amber-400 shadow-[0_0_18px_rgba(249,115,22,0.28)]';
  const graphicFeatureTitleClass = isLightTheme ? 'mb-2 text-lg font-black text-teal-300' : 'mb-2 text-lg font-black text-orange-300';
  const graphicMutedTextClass = 'text-sm text-white/76';
  const automationTitleClass = isLightTheme
    ? 'bg-gradient-to-r from-teal-300 via-sky-300 to-cyan-200 bg-clip-text text-3xl font-black text-transparent md:text-5xl'
    : 'bg-gradient-to-r from-orange-300 via-orange-500 to-amber-300 bg-clip-text text-3xl font-black text-transparent md:text-5xl';
  const automationBadgeClass = isLightTheme
    ? 'mb-4 inline-flex border border-teal-300/35 bg-teal-400/12 px-4 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-teal-200 shadow-[0_0_24px_rgba(20,184,166,0.14)]'
    : 'mb-4 inline-flex border border-orange-300/35 bg-orange-500/12 px-4 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-orange-200 shadow-[0_0_24px_rgba(249,115,22,0.14)]';
  const automationLogoClass = isLightTheme
    ? 'h-32 w-32 object-contain border border-teal-300/35 bg-white/92 p-3 shadow-[0_0_34px_rgba(20,184,166,0.2)]'
    : 'h-32 w-32 object-contain border border-orange-300/35 bg-white/92 p-3 shadow-[0_0_34px_rgba(249,115,22,0.22)]';
  const appTitleClass = isLightTheme
    ? 'mb-6 bg-gradient-to-r from-teal-300 via-sky-300 to-cyan-200 bg-clip-text text-4xl font-black text-transparent md:text-6xl'
    : 'mb-6 bg-gradient-to-r from-orange-300 via-orange-500 to-amber-300 bg-clip-text text-4xl font-black text-transparent md:text-6xl';
  const appCardClass = isLightTheme
    ? 'premium-card-reveal border border-teal-400/24 p-6 text-white shadow-[0_0_34px_rgba(20,184,166,0.12),inset_0_1px_0_rgba(204,251,241,0.1)]'
    : 'premium-card-reveal border border-orange-300/20 p-6 text-orange-50 shadow-[0_0_34px_rgba(249,115,22,0.1),inset_0_1px_0_rgba(255,237,213,0.08)]';
  const appFeatureTitleClass = isLightTheme ? 'mb-2 text-lg font-black text-teal-300' : 'mb-2 text-lg font-black text-orange-300';
  const appMutedTextClass = 'text-sm text-white/76';
  const appTechPillClass = isLightTheme
    ? 'border border-teal-300/25 bg-teal-400/10 px-3 py-1 text-sm font-bold text-teal-100 shadow-[0_0_18px_rgba(20,184,166,0.08)]'
    : 'border border-orange-300/25 bg-orange-500/10 px-3 py-1 text-sm font-bold text-orange-100 shadow-[0_0_18px_rgba(249,115,22,0.08)]';
  const staticSpotlightClass = isLightTheme
    ? 'pointer-events-none absolute rounded-full bg-teal-300/16 blur-3xl'
    : 'pointer-events-none absolute rounded-full bg-orange-500/16 blur-3xl';
  const automationCardClass = isLightTheme
    ? 'premium-card-reveal border border-teal-400/24 p-6 text-white shadow-[0_0_38px_rgba(20,184,166,0.13),inset_0_1px_0_rgba(204,251,241,0.1)]'
    : 'premium-card-reveal border border-orange-300/20 p-6 text-orange-50 shadow-[0_0_38px_rgba(249,115,22,0.12),inset_0_1px_0_rgba(255,237,213,0.08)]';
  const comingSoonRibbonClass = isLightTheme
    ? 'absolute right-4 top-4 z-20 border border-teal-200/40 bg-gradient-to-r from-teal-400/24 via-sky-300/18 to-cyan-200/20 px-4 py-2 text-[10px] font-black uppercase tracking-[0.22em] text-teal-100 shadow-[0_0_26px_rgba(20,184,166,0.18)]'
    : 'absolute right-4 top-4 z-20 border border-orange-200/40 bg-gradient-to-r from-orange-500/24 via-amber-300/18 to-yellow-200/20 px-4 py-2 text-[10px] font-black uppercase tracking-[0.22em] text-orange-100 shadow-[0_0_26px_rgba(249,115,22,0.18)]';
  const overlayGlassCardStyle: React.CSSProperties = {
    background: isLightTheme
      ? 'linear-gradient(145deg, rgba(3, 7, 18, 0.5), rgba(8, 47, 73, 0.28)), radial-gradient(circle at 18% 0%, rgba(45, 212, 191, 0.16), transparent 38%)'
      : 'linear-gradient(145deg, rgba(0, 0, 0, 0.62), rgba(67, 20, 7, 0.28)), radial-gradient(circle at 18% 0%, rgba(251, 146, 60, 0.16), transparent 38%)',
    transform: 'translateZ(0)',
    willChange: 'opacity, transform',
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden px-4 pb-16 pt-28" style={servicesShellStyle}>
      <div className={`pointer-events-none absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full blur-3xl ${isLightTheme ? 'bg-teal-400/20' : 'bg-orange-500/10'}`} />
      <div className="relative z-10 mx-auto w-full max-w-6xl">
      <p className={`mb-4 inline-flex border px-4 py-2 text-[11px] font-black uppercase tracking-[0.28em] ${isLightTheme ? 'border-teal-400/35 bg-white/70 text-teal-700 shadow-[0_0_28px_rgba(20,184,166,0.16)]' : 'border-orange-300/25 bg-orange-500/[0.08] text-orange-300 shadow-[0_0_28px_rgba(249,115,22,0.12)]'}`}>
        Digital craft systems
      </p>
      <h1 className="mb-4 w-full max-w-5xl text-left text-4xl font-black tracking-tight md:text-6xl" style={{ color: 'var(--text-primary)', textShadow: isLightTheme ? '0 0 38px rgba(20,184,166,0.16)' : '0 0 42px rgba(249,115,22,0.2)' }}>Our Services</h1>
      <p className="mb-12 w-full max-w-3xl text-left text-xl font-medium md:text-2xl" style={{ color: 'var(--text-secondary)' }}>
        We deliver world-class digital solutions with a relentless focus on quality, innovation, and detail. Explore our core offerings below.
      </p>
      <div className="mx-auto grid w-full max-w-5xl gap-5 md:grid-cols-2">
        <AnimatePresence>
          {!cardsDropped &&
            services.map((service) => (
              <CardSpotlight
                key={service.title}
                theme={theme}
                className={serviceSpotlightClass}
                initial={{ y: 0, opacity: 1 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ opacity: 0, filter: "blur(8px)" }}
                transition={{ duration: 0.28, ease: "easeOut" }}
              >
                <div
                  className="group/home relative flex min-h-[260px] cursor-pointer flex-col justify-center overflow-hidden rounded-none border p-7 text-left shadow-[0_0_34px_rgba(249,115,22,0.08)] backdrop-blur-xl transition-all duration-300"
                  style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}
                  onClick={() => handleCardClick(service.title)}
                >
                  <div className={serviceCardLineClass} />
                  <div className={serviceCardWashClass} />
                  {service.title === "Business Automation" && (
                    <span className={serviceBadgeClass}>
                      2 Apps
                    </span>
                  )}
                  <div className="relative z-10 mb-5 flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: 'linear-gradient(to right, var(--accent-primary), var(--accent-secondary))', boxShadow: serviceIconShadow }}>
                    <svg className="h-6 w-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={service.title === "Full Web Suite" ? "M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" : service.title === "Business Automation" ? "M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" : service.title === "Full Graphic Design" ? "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" : "M13 10V3L4 14h7v7l9-11h-7z"} />
                    </svg>
                  </div>
                  <h2 className="relative z-10 mb-3 text-2xl font-black leading-tight transition-colors duration-300" style={{ color: 'var(--text-primary)' }}>
                    {service.title}
                  </h2>
                  <p className="relative z-10 mb-4 text-base font-medium leading-6" style={{ color: 'var(--text-secondary)' }}>
                    {service.description}
                  </p>
                  <p className="relative z-10 text-sm font-bold uppercase leading-6 tracking-[0.12em]" style={{ color: 'var(--text-secondary)' }}>
                    {service.bullets.slice(0, 3).map((bullet, index) => (
                      <span key={index} className="transition-colors duration-200">
                        {bullet}
                        {index < Math.min(service.bullets.length, 3) - 1 && (
                            <span className={isLightTheme ? 'mx-2 text-teal-600/70' : 'mx-2 text-orange-400/70'}>•</span>
                        )}
                      </span>
                    ))}
                  </p>
                </div>
              </CardSpotlight>
            ))}
        </AnimatePresence>
      </div>
      </div>
      {/* Overlay for Web Development */}
      <AnimatePresence>
        {activeServiceOverlay === "Full Web Suite" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 30,
              background: isLightTheme
                ? "radial-gradient(circle at 72% 18%, rgba(20,184,166,0.24), transparent 32%), radial-gradient(circle at 20% 78%, rgba(56,189,248,0.14), transparent 34%), #020617"
                : "radial-gradient(circle at 72% 18%, rgba(249,115,22,0.24), transparent 32%), radial-gradient(circle at 20% 78%, rgba(245,158,11,0.12), transparent 34%), #020202",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "auto"
            }}
            className="overflow-y-auto"
          >
            {/* Background Vortex Effect */}
            <VortexDemo theme={theme} />
            {/* Return Button */}
            <motion.button
              onClick={handleReturn}
              className={serviceReturnButtonClass}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Return
            </motion.button>
            {/* Full Web Suite Content */}
            <div className="absolute inset-0 flex flex-col items-center justify-start md:justify-center overflow-y-auto z-10 p-4">
              <div className="text-center text-white max-w-5xl mt-32 md:mt-0">
                <motion.h2 
                  className={suiteTitleClass}
                  style={{ fontFamily: 'Century Gothic, Arial, sans-serif' }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.08, duration: 0.28, ease: "easeOut" }}
                >
                  Full Web Suite
                </motion.h2>
                <motion.p 
                  className="mb-8 text-xl text-white/76 md:text-2xl"
                  style={{ fontFamily: 'Century Gothic, Arial, sans-serif' }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.14, duration: 0.28, ease: "easeOut" }}
                >
                  Complete digital solutions that power your business
                </motion.p>
                
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.36, duration: 0.28, ease: "easeOut" }}
                >
                  <div className={suiteCardClass} style={overlayGlassCardStyle}>
                    <div className={suiteIconClass}>
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className={suiteFeatureTitleClass}>Web Development</h3>
                    <p className={suiteMutedTextClass}>Modern, responsive websites built with cutting-edge technologies</p>
                  </div>
                  
                  <div className={suiteCardClass} style={overlayGlassCardStyle}>
                    <div className={suiteIconClass}>
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                      </svg>
                    </div>
                    <h3 className={suiteFeatureTitleClass}>Web Design</h3>
                    <p className={suiteMutedTextClass}>Beautiful, user-centric designs that engage and convert</p>
                  </div>
                  
                  <div className={suiteCardClass} style={overlayGlassCardStyle}>
                    <div className={suiteIconClass}>
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <h3 className={suiteFeatureTitleClass}>SEO Optimization</h3>
                    <p className={suiteMutedTextClass}>Search engine optimization for maximum visibility</p>
                  </div>
                  
                  <div className={suiteCardClass} style={overlayGlassCardStyle}>
                    <div className={suiteIconClass}>
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h3 className={suiteFeatureTitleClass}>Performance</h3>
                    <p className={suiteMutedTextClass}>Lightning-fast loading speeds and optimized performance</p>
                  </div>
                  
                  <div className={suiteCardClass} style={overlayGlassCardStyle}>
                    <div className={suiteIconClass}>
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <h3 className={suiteFeatureTitleClass}>Security</h3>
                    <p className={suiteMutedTextClass}>Enterprise-grade security and data protection</p>
                  </div>
                  
                  <div className={suiteCardClass} style={overlayGlassCardStyle}>
                    <div className={suiteIconClass}>
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h3 className={suiteFeatureTitleClass}>Analytics</h3>
                    <p className={suiteMutedTextClass}>Comprehensive tracking and business insights</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
        {activeServiceOverlay === "Business Automation" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 30,
              background: isLightTheme
                ? "radial-gradient(circle at 72% 18%, rgba(20,184,166,0.22), transparent 32%), radial-gradient(circle at 20% 78%, rgba(56,189,248,0.12), transparent 34%), #020617"
                : "radial-gradient(circle at 72% 18%, rgba(249,115,22,0.22), transparent 32%), radial-gradient(circle at 20% 78%, rgba(245,158,11,0.12), transparent 34%), #020202",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "auto"
            }}
            className="overflow-y-auto"
          >
            <motion.button
              onClick={handleReturn}
              className={serviceReturnButtonClass}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Return
            </motion.button>
            <div className="relative flex min-h-full w-full flex-col items-center justify-start overflow-y-auto px-4 py-28 md:justify-center">
              <div className={`${staticSpotlightClass} left-[6%] top-[12%] h-72 w-72`} />
              <div className={`${staticSpotlightClass} bottom-[8%] right-[10%] h-96 w-96`} />
              <Spotlight
                fill={isLightTheme ? "#14b8a6" : "#f97316"}
                className="absolute inset-0 h-full w-full max-h-[62vh] max-w-5xl opacity-100"
              />

              <div className="relative z-10 mx-auto w-full max-w-6xl">
                <motion.div
                  className="mx-auto mb-8 max-w-4xl text-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.08, duration: 0.28, ease: "easeOut" }}
                >
                  <span className={automationBadgeClass} style={{ fontFamily: 'Century Gothic, Arial, sans-serif' }}>
                    Business automation suite
                  </span>
                  <h2 className={automationTitleClass} style={{ fontFamily: 'Century Gothic, Arial, sans-serif' }}>
                    Tender and Graft
                  </h2>
                  <p className="mx-auto mt-5 max-w-3xl text-lg font-semibold leading-8 text-white md:text-xl" style={{ fontFamily: 'Century Gothic, Arial, sans-serif' }}>
                    Purpose-built systems for hospitality operators, service teams, small businesses, and serial entrepreneurs who need proper tools without enterprise nonsense.
                  </p>
                </motion.div>

                <motion.div
                  className="grid gap-5 lg:grid-cols-[1.15fr_0.85fr]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.28, duration: 0.28, ease: "easeOut" }}
                >
                  <div className={`${automationCardClass} relative grid gap-6 pt-16 md:grid-cols-[auto_1fr] md:pt-6`} style={overlayGlassCardStyle}>
                    <span className={comingSoonRibbonClass}>Coming soon</span>
                    <img
                      src="/TT logo.png"
                      alt="Tender placeholder logo"
                      className={automationLogoClass}
                      style={{ aspectRatio: '1 / 1' }}
                    />
                    <div className="text-left">
                      <p className="mb-3 text-[10px] font-black uppercase tracking-[0.22em] text-white/45">Hospitality operations</p>
                      <h3 className={automationTitleClass} style={{ fontFamily: 'Century Gothic, Arial, sans-serif' }}>Tender</h3>
                      <p className="mt-4 text-base font-semibold leading-7 text-white/76">
                        The new automation product replacing the Table Tech alpha. Tender is built as two focused modules that can run together or separately.
                      </p>
                      <div className="mt-6 grid gap-4 md:grid-cols-2">
                        <div className="border border-white/10 bg-black/24 p-4">
                          <h4 className={appFeatureTitleClass}>Table Tender</h4>
                          <p className={appMutedTextClass}>Table-side ordering, service flow, kitchen visibility, and customer handling for restaurants.</p>
                        </div>
                        <div className="border border-white/10 bg-black/24 p-4">
                          <h4 className={appFeatureTitleClass}>Bar Tender</h4>
                          <p className={appMutedTextClass}>Fast bar ordering, tab handling, stock awareness, and cleaner service control for busy venues.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={`${automationCardClass} relative pt-16`} style={overlayGlassCardStyle}>
                    <span className={comingSoonRibbonClass}>Coming soon</span>
                    <p className="mb-3 text-[10px] font-black uppercase tracking-[0.22em] text-white/45">Mobile business command</p>
                    <h3 className={automationTitleClass} style={{ fontFamily: 'Century Gothic, Arial, sans-serif' }}>Graft</h3>
                    <p className="mt-4 text-base font-semibold leading-7 text-white/76">
                      Android and iPhone app for small businesses, service men, and serial entrepreneurs who need to quote, track, follow up, and keep momentum from the road.
                    </p>
                    <div className="mt-6 grid gap-3">
                      {['Jobs, clients, notes, and follow-ups in one place', 'Built for field work, quick decisions, and repeat business', 'Designed to grow into invoicing, CRM, AI admin, and team workflows'].map((item) => (
                        <div key={item} className="border border-white/10 bg-black/24 px-4 py-3 text-sm font-bold text-white/76">
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
        {activeServiceOverlay === "Full Graphic Design" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            style={{ position: "fixed", inset: 0, zIndex: 30, background: "#000", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "auto" }}
            className="overflow-y-auto"
          >
            {/* Background Video */}
            <video
              src="/Blip.mp4"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ zIndex: 1 }}
              muted
              autoPlay
              loop
              playsInline
            />
            {/* Return Button */}
            <motion.button
              onClick={handleReturn}
              className={serviceReturnButtonClass}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Return
            </motion.button>
            {/* Graphic Design Content */}
            <div className="absolute inset-0 flex flex-col items-center justify-start md:justify-center overflow-y-auto z-10 p-4">
              <div className="text-center text-white max-w-5xl mt-32 md:mt-0">
                <motion.div
                  className={`${graphicCardClass} mx-auto max-w-3xl`}
                  style={overlayGlassCardStyle}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.36, duration: 0.28, ease: "easeOut" }}
                >
                  <h2
                    className={graphicTitleClass}
                    style={{ fontFamily: 'Century Gothic, Arial, sans-serif' }}
                  >
                    Full Graphic Design
                  </h2>
                  <p
                    className="text-xl text-white/76 md:text-2xl"
                    style={{ fontFamily: 'Century Gothic, Arial, sans-serif' }}
                  >
                    Stunning visuals that capture attention and drive results
                  </p>
                </motion.div>
                
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.36, duration: 0.28, ease: "easeOut" }}
                >
                  <div className={graphicCardClass} style={overlayGlassCardStyle}>
                    <div className={graphicIconClass}>
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 11v5a2 2 0 002 2h6a2 2 0 002-2v-5M7 4h10l-1 9H8L7 4z" />
                      </svg>
                    </div>
                    <h3 className={graphicFeatureTitleClass}>Brand Identity</h3>
                    <p className={graphicMutedTextClass}>Logos, business cards, letterheads, and complete brand systems</p>
                  </div>
                  
                  <div className={graphicCardClass} style={overlayGlassCardStyle}>
                    <div className={graphicIconClass}>
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <h3 className={graphicFeatureTitleClass}>Print Design</h3>
                    <p className={graphicMutedTextClass}>Flyers, brochures, menus, catalogues, and packaging design</p>
                  </div>
                  
                  <div className={graphicCardClass} style={overlayGlassCardStyle}>
                    <div className={graphicIconClass}>
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className={graphicFeatureTitleClass}>Digital Design</h3>
                    <p className={graphicMutedTextClass}>Web visuals, social media content, banners, and online adverts</p>
                  </div>
                  
                  <div className={graphicCardClass} style={overlayGlassCardStyle}>
                    <div className={graphicIconClass}>
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className={graphicFeatureTitleClass}>Video Production</h3>
                    <p className={graphicMutedTextClass}>Filming, editing, animation, and promotional video content</p>
                  </div>
                  
                  <div className={graphicCardClass} style={overlayGlassCardStyle}>
                    <div className={graphicIconClass}>
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <h3 className={graphicFeatureTitleClass}>Signage & Display</h3>
                    <p className={graphicMutedTextClass}>Indoor/outdoor signage, point-of-sale displays, vehicle branding</p>
                  </div>
                  
                  <div className={graphicCardClass} style={overlayGlassCardStyle}>
                    <div className={graphicIconClass}>
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </div>
                    <h3 className={graphicFeatureTitleClass}>Web Detailing</h3>
                    <p className={graphicMutedTextClass}>UI elements, layout design, content formatting, and styling</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
        {activeServiceOverlay === "App Dev" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            style={{ position: "fixed", inset: 0, zIndex: 30, background: "#000", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "auto" }}
            className="overflow-y-auto"
          >
            {/* Background Video */}
            <video
              src="/App board.mp4"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ zIndex: 1 }}
              muted
              autoPlay
              loop
              playsInline
            />
            {/* App Development Content */}
            <div className="absolute inset-0 flex flex-col md:items-center md:justify-center z-10 p-4 overflow-y-auto">
              <div className="text-center text-white max-w-4xl mx-auto mt-32 md:mt-0">
                <motion.h2 
                  className={appTitleClass}
                  style={{ fontFamily: 'Century Gothic, Arial, sans-serif' }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.08, duration: 0.28, ease: "easeOut" }}
                >
                  App Development
                </motion.h2>
                <motion.p 
                  className="mb-8 text-xl text-white/76 md:text-2xl"
                  style={{ fontFamily: 'Century Gothic, Arial, sans-serif' }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.14, duration: 0.28, ease: "easeOut" }}
                >
                  Cutting-edge mobile and web applications built for the future
                </motion.p>
                
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.36, duration: 0.28, ease: "easeOut" }}
                >
                  <div className={appCardClass} style={overlayGlassCardStyle}>
                    <div className={graphicIconClass}>
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className={appFeatureTitleClass}>Mobile Apps</h3>
                    <p className={appMutedTextClass}>Native iOS & Android applications with seamless performance</p>
                  </div>
                  
                  <div className={appCardClass} style={overlayGlassCardStyle}>
                    <div className={graphicIconClass}>
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className={appFeatureTitleClass}>Web Apps</h3>
                    <p className={appMutedTextClass}>Progressive web applications that work across all devices</p>
                  </div>
                  
                  <div className={appCardClass} style={overlayGlassCardStyle}>
                    <div className={graphicIconClass}>
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                      </svg>
                    </div>
                    <h3 className={appFeatureTitleClass}>Backend APIs</h3>
                    <p className={appMutedTextClass}>Robust server infrastructure and database architecture</p>
                  </div>
                  
                  <div className={appCardClass} style={overlayGlassCardStyle}>
                    <div className={graphicIconClass}>
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <h3 className={appFeatureTitleClass}>Custom AI Pipelines</h3>
                    <p className={appMutedTextClass}>Tailored AI implementations and bespoke solutions designed for your specific use cases</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  className={`${appCardClass} mt-12`}
                  style={overlayGlassCardStyle}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.42, duration: 0.28, ease: "easeOut" }}
                >
                  <h3 className={appFeatureTitleClass}>Any Code. Any Stack. Any Issue.</h3>
                  <p className="mx-auto mb-5 max-w-2xl text-sm font-semibold leading-6 text-white/70">
                    We are not locked to one framework or one fashionable stack. We help ship, repair, extend, and integrate the software your business actually needs.
                  </p>
                  <div className="flex flex-wrap justify-center gap-3">
                    {['Any codebase', 'Any stack', 'Bug fixing', 'Legacy rescue', 'API integrations', 'Mobile apps', 'Backend systems', 'AI workflows'].map((tech) => (
                      <span key={tech} className={appTechPillClass}>
                        {tech}
                      </span>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
            {/* Return Button */}
            <motion.button
              onClick={handleReturn}
              className={serviceReturnButtonClass}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Return
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ServicesPage;

function SimpleGraphicSequence() {
  const [showBlip, setShowBlip] = React.useState(false);
  const [fadeOutPng, setFadeOutPng] = React.useState(false);
  const [wordIndex, setWordIndex] = React.useState(0);
  const [showServices, setShowServices] = React.useState(false);
  const words = ["Graphics", "Detailing", "& Video"];

  React.useEffect(() => {
    if (wordIndex < words.length) {
      const timer = setTimeout(() => setWordIndex(wordIndex + 1), 2000);
      return () => clearTimeout(timer);
    } else {
      // After last word, start the PNG/Blip sequence
      const timer = setTimeout(() => {
        setShowBlip(true);
        setFadeOutPng(true);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [wordIndex]);

  React.useEffect(() => {
    if (showBlip) {
      const timer = setTimeout(() => setShowServices(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [showBlip]);

  return (
    <div className="absolute inset-0 w-full h-full flex items-center justify-center">
      {/* Flash words in center before sequence */}
      {wordIndex < words.length && (
        <span
          className="absolute z-50 text-white text-4xl md:text-6xl font-bold"
          style={{ fontFamily: 'Century Gothic, Arial, sans-serif', transition: 'opacity 0.5s' }}
        >
          {words[wordIndex]}
        </span>
      )}
      {/* Blip.mp4 video in background after 3s */}
      {showBlip && (
        <video
          src="/Blip.mp4"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ zIndex: 1 }}
          muted
          autoPlay
          loop
          playsInline
        />
      )}
      {/* PNG always on top, fades out when Blip appears */}
      <img
        src="/cherry.png"
        alt="Cherry Background"
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
        style={{ zIndex: 2, opacity: fadeOutPng ? 0 : 1, transition: 'opacity 1s' }}
        draggable={false}
      />
      {/* Services list displayed after intro sequence */}
      {showServices && (
        <div className="absolute inset-0 flex items-center justify-center z-30 p-4" style={{ fontFamily: 'Century Gothic, Arial, sans-serif' }}>
          <div className="bg-black/50 backdrop-blur-sm rounded-xl p-8 max-w-4xl text-white text-left space-y-6">
            <h2 className="text-3xl md:text-5xl font-bold text-center">Our Services Include:</h2>
            <ul className="list-disc list-inside space-y-2 text-lg md:text-2xl">
              <li><span className="font-semibold">Graphic Design:</span> Logos, business cards, flyers, menus, brochures, packaging, catalogues</li>
              <li><span className="font-semibold">Digital Design:</span> Web visuals, banners, social media content, emailers, online adverts</li>
              <li><span className="font-semibold">Signage & Display:</span> Indoor/outdoor signage, point-of-sale displays, vehicle branding</li>
              <li><span className="font-semibold">Video Production:</span> Filming, editing, animation, and promotional content</li>
              <li><span className="font-semibold">Web Detailing:</span> UI elements, layout design, content formatting, and styling</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}